import React, { useState, useEffect } from 'react';
import { withRouter } from 'react-router-dom';
import { observer, inject } from 'mobx-react';
import { Modal, Placeholder } from 'semantic-ui-react';
import _ from 'lodash';
import styled from 'styled-components';
import BasicProgressPlaceholder from '@aws-ee/base-ui/dist/parts/helpers/BasicProgressPlaceholder';
import ErrorBox from '@aws-ee/base-ui/dist/parts/helpers/ErrorBox';
import QuizContent from './parts/QuizContent';
import ProgressEnum from '../../models/learning/ProgressEnum';

const QuizModal = styled(Modal)`
  width: 100% !important;
  height: 100%;
  margin: 0px !important;
  top: 0px;
  background: #f5f5f5 !important;
  overflow: hidden;
`;

const Quiz = styled.div`
  height: 100%;
`;

const QuizHeader = styled.div`
  height: 50px;
  margin: 0px 150px 64px 150px;
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    width: 210px;
    position: absolute;
    left: 24px;
    cursor: pointer;
  }

  h1 {
    font-family: 'IBM Plex Sans';
    font-weight: 700;
    font-size: 20px;
    line-height: 20px;
    color: #2e2d29;
    margin: 0px;
  }
`;

const QuizPage = ({ match, assets, learningStore, quizStore }) => {
  const params = match.params;

  const [isCertificatePath, setIsCertificatePath] = useState(false);
  const [module, setModule] = useState(null);
  const [submissionResult, setSubmissionResult] = useState(null);
  const [quizInstance, setQuizInstance] = useState(null);
  const [loading, setLoading] = useState();
  const [error, setError] = useState();

  useEffect(() => {
    const getUserLearning = async id => {
      setLoading(true);
      const userLearning = await learningStore.getUserLearning(id);
      if (!_.isNil(userLearning) && userLearning.id === params.userLearningId && userLearning.paths.length > 0) {
        const pathIndex = userLearning.paths.findIndex(m => m.id === params.pathId);
        setIsCertificatePath(userLearning.paths[pathIndex].isCertificatePath);
        if (pathIndex > -1) {
          const moduleIndex = userLearning.paths[pathIndex].modules.findIndex(m => m.id === params.moduleId);
          if (moduleIndex > -1) {
            const module = userLearning.paths[pathIndex].modules[moduleIndex];
            setModule(module);
          } else setError(`Module with id ${params.moduleId} doesn't exist`);
        } else setError(`Path with id ${params.pathId} doesn't exist`);
      } else setError(`User learning with id ${params.userLearningId} doesn't exist`);
      setLoading(false);
    };

    getUserLearning(params.userLearningId).catch(error => {
      setLoading(false);
      setError(error.message);
    });
  }, [params]);

  useEffect(() => {
    const getQuizSubmissionResult = async id => {
      setLoading(true);
      const submissionResult = await quizStore.getQuizSubmissionResult(id);
      setSubmissionResult(submissionResult);
      setQuizInstance(submissionResult.quizInstance);
      setLoading(false);
    };
    const createQuizInstance = async id => {
      setLoading(true);
      const instance = await quizStore.createQuizInstance(id);
      setQuizInstance(instance);
      setLoading(false);
    };

    if (!_.isNil(module) && module.progress === ProgressEnum.Completed) {
      getQuizSubmissionResult(module.quizId).catch(error => {
        setLoading(false);
        setError(error.message);
      });
    } else if (!_.isNil(module) && !_.isNil(module.quizId))
      createQuizInstance(module.quizId).catch(error => {
        setLoading(false);
        setError(error.message);
      });
    else if (!_.isNil(module) && !_.isNil(module.certificateQuizId))
      createQuizInstance(module.certificateQuizId).catch(error => {
        setLoading(false);
        setError(error.message);
      });
  }, [module]);

  let content = null;
  if (error) {
    content = <ErrorBox error={error} className="mt3 mr0 ml0" />;
  } else if (loading) {
    content = <BasicProgressPlaceholder segmentCount={5} />;
  } else if (quizInstance || submissionResult) {
    content = (
      <QuizContent
        isCertificatePath={isCertificatePath}
        quizInstance={quizInstance}
        userLearningId={params.userLearningId}
        pathId={params.pathId}
        moduleId={params.moduleId}
        defaultSubmissionResult={submissionResult}
      />
    );
  }

  return (
    <QuizModal className="pt3 px3" open>
      <Quiz className="flex flex-column">
        <QuizHeader>
          <img
            src={assets.images.logoImage}
            alt="Stanford Data Ocean Logo"
            onClick={() => {
              window.location = window.location.origin;
            }}
          />
          {!_.isNil(module) ? (
            <h1>{module.name}</h1>
          ) : (
            <div className="col-3">
              <Placeholder>
                <Placeholder.Header>
                  <Placeholder.Line />
                </Placeholder.Header>
              </Placeholder>
            </div>
          )}
        </QuizHeader>
        {content}
      </Quiz>
    </QuizModal>
  );
};

export default inject('learningStore', 'quizStore')(withRouter(observer(QuizPage)));
